/**
 * Shared "open a project" flow used by ProjectSelectModal (in-editor switcher)
 * and DashboardPage (post-login landing). Encapsulates:
 *   - calling loadProjectFromServer
 *   - syncing the loaded mapCenter/zoom/bearing/pitch into mapStore
 *   - flying the map to the saved view when the editor map is mounted
 *   - surfacing a success / "is empty" notification
 *
 * The dashboard caller additionally navigates to /editor after this resolves;
 * the modal caller closes itself. Navigation is intentionally not part of the
 * hook so both callers can wire their own follow-through.
 */

import { useCallback, useState } from 'react';
import { useProjectStore } from '../stores/projectStore';
import { useMapStore } from '../stores/mapStore';
import { useUIStore } from '../stores/uiStore';
import type { SiteListItem } from '../services/projectApi';

interface OpenProjectResult {
  loadingId: string | null;
  loadingLabel: string;
  error: string | null;
  openProject: (site: Pick<SiteListItem, 'id' | 'name' | 'client_name'>) => Promise<boolean>;
}

function syncMapView(data: Record<string, unknown>): void {
  const mapCenter = data.mapCenter as [number, number] | undefined;
  const mapZoom = data.mapZoom as number | undefined;
  if (!mapCenter && mapZoom === undefined) return;
  const ms = useMapStore.getState();
  ms.setView({
    center: mapCenter,
    zoom: mapZoom,
    bearing: data.mapBearing as number | undefined,
    pitch: data.mapPitch as number | undefined,
  });
  if (ms.map && mapCenter) {
    ms.map.flyTo({
      center: mapCenter,
      zoom: mapZoom,
      bearing: (data.mapBearing as number | undefined) ?? 0,
      pitch: (data.mapPitch as number | undefined) ?? 0,
      duration: 1500,
    });
  }
}

export function useOpenProject(): OpenProjectResult {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [loadingLabel, setLoadingLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { loadProjectFromServer } = useProjectStore();
  const { addNotification } = useUIStore();

  const openProject = useCallback(
    async (site: Pick<SiteListItem, 'id' | 'name' | 'client_name'>): Promise<boolean> => {
      setLoadingId(site.id);
      setLoadingLabel(`Downloading "${site.name}"...`);
      setError(null);
      try {
        const projectData = await loadProjectFromServer(site.id, site.name, site.client_name);
        syncMapView(projectData as unknown as Record<string, unknown>);
        const isEmpty =
          !((projectData as { indications?: unknown[] }).indications?.length) &&
          !((projectData as { overlays?: unknown[] }).overlays?.length) &&
          !((projectData as { orthophotos?: unknown[] }).orthophotos?.length);
        if (isEmpty) {
          addNotification({
            type: 'info',
            title: `"${site.name}" is empty`,
            message: 'No data has been saved to this project yet',
          });
        } else {
          addNotification({ type: 'success', title: `Opened "${site.name}"` });
        }
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to open project';
        setError(msg);
        return false;
      } finally {
        setLoadingId(null);
        setLoadingLabel('');
      }
    },
    [loadProjectFromServer, addNotification]
  );

  return { loadingId, loadingLabel, error, openProject };
}
